import { AuthSession } from '@Shared/auth/auth-session';
import {
  httpRequestJson,
  httpRequestText,
  type HttpRequestConfig,
  type HttpResponse,
} from './httpClient';

export interface RestClientOptions {
  baseUrl: string;
  defaultHeaders?: Record<string, string>;
  defaultTimeoutMs?: number;
  getAuthToken?: () => string | null;
}

export class RestClient {
  private readonly baseUrl: URL;
  private readonly defaultHeaders: Record<string, string>;
  private readonly defaultTimeoutMs?: number;
  private readonly getAuthToken?: () => string | null;

  constructor(options: RestClientOptions) {
    this.baseUrl = new URL(options.baseUrl, window.location.origin);
    this.defaultHeaders = options.defaultHeaders ?? {};
    this.defaultTimeoutMs = options.defaultTimeoutMs;
    this.getAuthToken = options.getAuthToken;
  }

  private buildUrl(path: string | URL): string {
    if (path instanceof URL) {
      return path.toString();
    }

    const url = new URL(path, this.baseUrl);
    return url.toString();
  }

  private mergeHeaders(extraHeaders?: HeadersInit, hasBody = false): HeadersInit {
    const merged = new Headers(this.defaultHeaders);

    if (extraHeaders) {
      const extra = new Headers(extraHeaders);
      extra.forEach((value, key) => {
        merged.set(key, value);
      });
    }

    const token = this.getAuthToken?.();
    if (token) {
      merged.set('Authorization', `Bearer ${token}`);
    }

    merged.set('X-Correlation-ID', crypto.randomUUID());

    if (hasBody && !merged.has('Content-Type')) {
      merged.set('Content-Type', 'application/json');
    }
    if (!merged.has('Accept')) {
      merged.set('Accept', 'application/json');
    }

    return merged;
  }

  private buildRequestConfig(
    path: string | URL,
    config?: Omit<HttpRequestConfig, 'url'>
  ): HttpRequestConfig {
    const hasBody = config?.body != null;
    return {
      url: this.buildUrl(path),
      method: config?.method,
      headers: this.mergeHeaders(config?.headers, hasBody),
      body: config?.body ?? null,
      timeoutMs: config?.timeoutMs ?? this.defaultTimeoutMs,
    };
  }

  private async requestJson<T>(
    method: HttpRequestConfig['method'],
    path: string | URL,
    body?: unknown,
    config?: Omit<HttpRequestConfig, 'url' | 'method' | 'body'>
  ): Promise<HttpResponse<T>> {
    const requestConfig: HttpRequestConfig = this.buildRequestConfig(path, {
      ...config,
      method,
      body: body === undefined || body === null ? null : JSON.stringify(body),
    });

    return await httpRequestJson<T>(requestConfig);
  }

  public async getText(
    path: string | URL,
    config?: Omit<HttpRequestConfig, 'url' | 'method' | 'body'>
  ): Promise<HttpResponse<string>> {
    const requestConfig = this.buildRequestConfig(path, { method: 'GET', ...config });
    return await httpRequestText(requestConfig);
  }

  public async get<T>(
    path: string | URL,
    config?: Omit<HttpRequestConfig, 'url' | 'method' | 'body'>
  ): Promise<HttpResponse<T>> {
    return await this.requestJson<T>('GET', path, undefined, config);
  }

  public async post<T, B = unknown>(
    path: string | URL,
    body?: B,
    config?: Omit<HttpRequestConfig, 'url' | 'method' | 'body'>
  ): Promise<HttpResponse<T>> {
    return await this.requestJson<T>('POST', path, body, config);
  }

  public async put<T, B = unknown>(
    path: string | URL,
    body?: B,
    config?: Omit<HttpRequestConfig, 'url' | 'method' | 'body'>
  ): Promise<HttpResponse<T>> {
    return await this.requestJson<T>('PUT', path, body, config);
  }

  public async patch<T, B = unknown>(
    path: string | URL,
    body?: B,
    config?: Omit<HttpRequestConfig, 'url' | 'method' | 'body'>
  ): Promise<HttpResponse<T>> {
    return await this.requestJson<T>('PATCH', path, body, config);
  }

  public async delete<T>(
    path: string | URL,
    config?: Omit<HttpRequestConfig, 'url' | 'method' | 'body'>
  ): Promise<HttpResponse<T>> {
    return await this.requestJson<T>('DELETE', path, undefined, config);
  }
}

const authSession = AuthSession.getInstance();

export const apiRestClient = new RestClient({
  baseUrl: `${import.meta.env.VITE_API_URL}/`,
  getAuthToken: () => authSession.getIdToken() ?? null,
});
